import type { ReactNode } from 'react';
import { useStepper } from '@/shared/app-stepper-form/hooks/use-stepper';
import { useConfirmation } from '@/shared/app-stepper-form/hooks/use-confirmation';
import { styles } from '@/shared/app-stepper-form/styles';
import { StepRailColumn } from '@/shared/app-stepper-form/parts/step-rail-column';
import { StepperDialogs } from '@/shared/app-stepper-form/parts/stepper-dialogs';
import { StepperPanel } from '@/shared/app-stepper-form/parts/stepper-panel';
import type { AppStepperFormProps } from '@/shared/app-stepper-form/types';

/**
 * The reusable stepper layout.
 *
 * OWNS: step array, navigation, per-step validation gating, the two
 * confirmations, and the draft-versus-publish validation split. DOES NOT OWN:
 * what a step contains, what "valid" means, or where leaving goes — all three
 * are domain knowledge supplied by the caller, because this layout is not
 * allowed to have any.
 *
 * THE LAYOUT
 * ----------
 * A rail card on the left from `lg`, and a fixed-height panel on the right whose
 * body scrolls and whose footer is pinned. The panel is the same height on every
 * step, so the primary action never moves and long content scrolls inside the
 * card instead of growing the page. Below `lg` the rail collapses to a compact
 * progress bar and the panel takes the full width.
 *
 * See `styles/index.ts` for why the height is expressed as `min-h-0 flex-1`
 * rather than a viewport unit.
 *
 * Generalised from the donor product create page, whose five steps were tangled
 * with clothing-specific editors. The five steps do not survive contact with a
 * booking or a jeweller, so only the MECHANISM is reusable.
 */
export function AppStepperForm(props: AppStepperFormProps): ReactNode {
  const stepper = useStepper({
    steps: props.steps,
    onSubmit: props.onSubmit,
    ...(props.onStepChange ? { onStepChange: props.onStepChange } : {}),
  });

  const confirmation = useConfirmation({
    hasUnsavedChanges: props.hasUnsavedChanges ?? false,
    onCancel: props.onCancel,
    onSubmit: props.onSubmit,
  });

  const { active, activeIndex } = stepper;
  if (!active) return null;

  return (
    <section className={styles.page} aria-label={props.title}>
      <header className={styles.header}>
        <h1 className={styles.title}>{props.title}</h1>
      </header>

      <div className={styles.body}>
        <StepRailColumn
          steps={props.steps}
          states={stepper.states}
          activeIndex={activeIndex}
          furthestReached={stepper.furthestReached}
          onJump={stepper.jumpTo}
        />

        <StepperPanel
          step={active}
          steps={props.steps}
          states={stepper.states}
          index={activeIndex}
          furthestReached={stepper.furthestReached}
          isFirst={activeIndex === 0}
          isLast={stepper.isLast}
          canAdvance={stepper.canAdvance}
          isSubmitting={props.isSubmitting ?? false}
          submitLabel={props.submitLabel ?? 'Submit'}
          cancelLabel={props.cancelLabel ?? 'Cancel'}
          submitError={props.submitError}
          onCancel={confirmation.requestCancel}
          onBack={stepper.attemptBack}
          onAdvance={stepper.attemptAdvance}
          onSubmit={props.confirmSubmit ? confirmation.requestSubmit : () => void stepper.submit()}
          onJump={stepper.jumpTo}
        />

        {props.aside ? <div className={styles.aside}>{props.aside}</div> : null}
      </div>

      <StepperDialogs
        pending={confirmation.pending}
        hasUnsavedChanges={props.hasUnsavedChanges ?? false}
        confirmSubmit={props.confirmSubmit}
        isSubmitting={props.isSubmitting ?? false}
        onDismiss={confirmation.dismiss}
        onConfirmDiscard={confirmation.confirmDiscard}
        onConfirmPublish={confirmation.confirmPublish}
      />
    </section>
  );
}
